import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shaGetStatusForVisit, shaGetStatusForAdmission, shaSearchBenefits } from '../../services/sha.api';
import api from '../../services/api';
import { isShaCoveredPatient } from '../../utils/shaPatient';

/**
 * Reusable "File SHA Claim" action for any module that isn't IPD/OPD (which already
 * have their own auto-claim wiring — see ShaIntegrationService.onPatientAdmitted() /
 * onOutpatientVisitCreated()). Renal dialysis sessions, ANC visits, psych assessments,
 * palliative care cases, lab orders, and radiology orders have no such auto-trigger
 * (see audit: none of them call postCharge() or any SHA service), so staff previously
 * had no path from these screens into a claim except manually opening the SHA module
 * and typing IDs from memory.
 *
 * This does two things a manual claim doesn't get for free:
 *  1. Shows the claim's live status if one already exists for this encounter (ShaClaim
 *     .visitId is a free-form UUID column with no FK — reused here as a generic
 *     "source encounter id", not literally a `patient_visits` row).
 *  2. On first click, looks up a suggested DHA intervention code from the 724-entry
 *     Benefits Matrix by service name, and deep-links into the New Claim wizard with
 *     visitId/crId/interventionCode pre-filled — reusing the wizard's own
 *     crId+interventionCode auto-scenario-detection (SHAClaimPage.tsx) instead of
 *     making staff hunt for the code themselves. Staff still completes/reviews the
 *     claim before anything goes to DHA — this only removes the blank-page start.
 */
export interface FileShaClaimButtonProps {
  patientId?: string;
  patientName?: string;
  patientCrId?: string;
  encounterId?: string;
  serviceName: string;
  className?: string;
  serviceType?: 'INPATIENT' | 'OUTPATIENT' | 'EMERGENCY';
  admissionId?: string;
  from?: string;
  interventionCode?: string;
}

const STATUS_COLOR: Record<string, string> = {
  CLAIM_CREATED: 'bg-emerald-100 text-emerald-700',
  BILLING_ADDED: 'bg-blue-100 text-blue-700',
  DISCHARGED: 'bg-teal-100 text-teal-700',
  SUBMITTED: 'bg-purple-100 text-purple-700',
  PAID: 'bg-green-100 text-green-700',
  PARTIALLY_PAID: 'bg-amber-100 text-amber-700',
  PREVIEWED: 'bg-indigo-100 text-indigo-700',
  ELIGIBILITY_CONFIRMED: 'bg-cyan-100 text-cyan-700',
  INITIATED: 'bg-amber-100 text-amber-700',
  NOT_ELIGIBLE: 'bg-red-100 text-red-700',
  CREATING_PREAUTH: 'bg-orange-100 text-orange-700',
  PENDING_DOCTOR_APPROVAL: 'bg-orange-100 text-orange-700',
  CLAIM_REJECTED: 'bg-red-200 text-red-900 font-black',
  PREAUTH_REJECTED: 'bg-red-200 text-red-900 font-black',
};

export default function FileShaClaimButton({
  patientId, patientName, patientCrId, encounterId, serviceName, className,
  serviceType, admissionId, from, interventionCode,
}: FileShaClaimButtonProps) {
  const nav = useNavigate();
  const [status, setStatus] = useState<{ hasClaim?: boolean; claimId?: string; status?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [shaCovered, setShaCovered] = useState<boolean | null>(null);
  const unidentifiedEr = !patientId && (serviceType === 'EMERGENCY' || from === 'emergency');

  useEffect(() => {
    let alive = true;
    if (patientCrId || unidentifiedEr) {
      setShaCovered(true);
      return;
    }
    if (!patientId) {
      setShaCovered(false);
      return;
    }
    api.getPatient(patientId).then((r: any) => {
      const p = r?.data?.data || r?.data || r;
      if (alive) setShaCovered(isShaCoveredPatient(p));
    }).catch(() => { if (alive) setShaCovered(false); });
    return () => { alive = false; };
  }, [patientId, patientCrId, unidentifiedEr]);

  useEffect(() => {
    let alive = true;
    if (!encounterId && !admissionId) return;
    const load = async () => {
      const checks: Promise<any>[] = [];
      if (encounterId) checks.push(shaGetStatusForVisit(encounterId).catch(() => ({ data: null })));
      if (admissionId) checks.push(shaGetStatusForAdmission(admissionId).catch(() => ({ data: null })));
      const results = await Promise.all(checks);
      const hit = results.map(r => r?.data).find((d: any) => d?.hasClaim) || results[0]?.data;
      if (alive) setStatus(hit || null);
    };
    load();
    return () => { alive = false; };
  }, [encounterId, admissionId]);

  const handleClick = async () => {
    if (status?.hasClaim && status.claimId) {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (patientId) qs.set('patientId', patientId);
      if (admissionId) qs.set('admissionId', admissionId);
      nav(`/sha/claims/${status.claimId}${qs.toString() ? `?${qs}` : ''}`);
      return;
    }
    setLoading(true);
    let code = interventionCode || '';
    if (!code) {
    try {
      const r = await shaSearchBenefits(serviceName, 1);
      const results = r?.data?.results || r?.data || [];
      const match = Array.isArray(results) ? results[0] : null;
      code = match?.interventionCode || match?.intervention_code || '';
    } catch {
      // No match / lookup failed — wizard still works, staff picks the code manually
    }
    }
    setLoading(false);

    const st = serviceType || (admissionId ? 'INPATIENT' : from === 'emergency' ? 'EMERGENCY' : 'OUTPATIENT');
    const src = from || (st === 'EMERGENCY' ? 'emergency' : st === 'INPATIENT' ? 'ipd' : 'opd');
    const qs = new URLSearchParams({ serviceType: st, from: src });
    if (patientId) qs.set('patientId', patientId);
    if (patientName) qs.set('patientName', patientName);
    const encounterIsPatient = encounterId && patientId && encounterId === patientId;
    if (st === 'EMERGENCY' && encounterId) qs.set('emergencyCaseId', encounterId);
    else if (encounterId && !encounterIsPatient) qs.set('visitId', encounterId);
    if (!patientId && st === 'EMERGENCY') qs.set('unidentified', '1');
    if (admissionId) qs.set('admissionId', admissionId);
    if (patientCrId) qs.set('crId', patientCrId);
    if (code) qs.set('interventionCode', code);
    nav(`/sha/new-claim?${qs.toString()}`);
  };

  const badgeColor = status?.status ? (STATUS_COLOR[status.status] || 'bg-slate-100 text-slate-600') : '';
  if (!status?.hasClaim && shaCovered === false) return null;
  if (!status?.hasClaim && shaCovered === null && !unidentifiedEr && !patientCrId) return null;

  return (
    <div className="flex flex-col gap-1 items-start">
      {status?.hasClaim && status.status && (
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold w-fit ${badgeColor}`}>
          {status.status.replace(/_/g, ' ')}
        </span>
      )}
      <button
        onClick={handleClick}
        disabled={loading}
        className={className || 'text-xs px-2 py-0.5 bg-[#039089] text-white rounded-lg font-semibold hover:bg-[#02357A] transition-all w-fit disabled:opacity-50'}
      >
        {loading ? '...' : status?.hasClaim ? '→ Open SHA Claim' : '+ File SHA Claim'}
      </button>
    </div>
  );
}
